import {KeyboardEvent, useRef} from "react";
import {classNames} from "primereact/utils";
import "./TwoFactorCodeField.scss";

type TwoFactorCodeFieldProps = {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    invalid?: boolean;
    autoFocus?: boolean;
    onSubmit?: () => void;
};

const CODE_LENGTH = 6;

export const TwoFactorCodeField = ({id, value, onChange, invalid, autoFocus, onSubmit}: TwoFactorCodeFieldProps) => {
    const inputs = useRef<Array<HTMLInputElement | null>>([]);
    const digits = Array.from({length: CODE_LENGTH}, (_, index) => value[index] || "");

    const commit = (next: string[]) => {
        onChange(next.join("").replace(/\D/g, "").slice(0, CODE_LENGTH));
    };

    const focusAt = (index: number) => {
        const target = inputs.current[Math.max(0, Math.min(index, CODE_LENGTH - 1))];
        target?.focus();
        target?.select();
    };

    const writeFrom = (index: number, raw: string) => {
        const chars = raw.replace(/\D/g, "");
        if (!chars) {
            const next = [...digits];
            next[index] = "";
            commit(next);
            return;
        }
        const next = [...digits];
        chars.split("").slice(0, CODE_LENGTH - index).forEach((char, offset) => {
            next[index + offset] = char;
        });
        commit(next);
        focusAt(Math.min(index + chars.length, CODE_LENGTH - 1));
    };

    const onKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Backspace") {
            event.preventDefault();
            const next = [...digits];
            if (digits[index]) {
                next[index] = "";
                commit(next);
                return;
            }
            if (index > 0) {
                next[index - 1] = "";
                commit(next);
                focusAt(index - 1);
            }
            return;
        }
        if (event.key === "ArrowLeft" && index > 0) {
            event.preventDefault();
            focusAt(index - 1);
        }
        if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
            event.preventDefault();
            focusAt(index + 1);
        }
        if (event.key === "Enter") {
            event.preventDefault();
            onSubmit?.();
        }
    };

    return (
        <div className={classNames("two-factor-code", {"two-factor-code--invalid": invalid})} id={id}>
            {digits.map((digit, index) => (
                <input
                    key={index}
                    ref={(node) => {
                        inputs.current[index] = node;
                    }}
                    className="two-factor-code__cell"
                    value={digit}
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    autoFocus={autoFocus && index === 0}
                    aria-label={`Цифра ${index + 1}`}
                    maxLength={index === 0 ? CODE_LENGTH : 1}
                    onChange={(event) => writeFrom(index, event.target.value)}
                    onKeyDown={(event) => onKeyDown(index, event)}
                    onPaste={(event) => {
                        event.preventDefault();
                        writeFrom(0, event.clipboardData.getData("text"));
                    }}
                    onFocus={(event) => event.target.select()}
                />
            ))}
        </div>
    );
};
